import { useState } from 'react';

import type { OrderInvoice } from '@/entities/invoice';
import type { ReportInvoiceChoice, ReportRecipient } from '@/entities/report';

/** Wat een verstuurdialoog over de factuur weet; `undefined` zolang het geladen wordt, `null` als het niet lukte. */
export type InvoiceOptionState = OrderInvoice | null | undefined;

export interface InvoiceEnclosureSetup {
    option: InvoiceOptionState;
    /** Het standaard eigen bericht uit de instellingen: daar begint het tekstveld mee. */
    defaultMessage: string;
}

/**
 * De keuze "Factuur meesturen" in één verstuurdialoog: standaard aan als de factuur
 * mee kan (er is er een, of hij kan uit de diensten worden gemaakt) en er een klant
 * met een e-mailadres tussen de ontvangers staat; het eigen bericht begint met de
 * standaardtekst uit de instellingen. Wat je aanpast geldt alleen voor deze verzending.
 *
 * Komt de stand van de factuur later binnen dan de dialoog opent, dan volgt de keuze
 * vanzelf, tot je hem zelf omzet.
 */
export function useInvoiceChoice(
    setup: InvoiceEnclosureSetup | undefined,
    recipients: readonly ReportRecipient[],
) {
    const [include, setInclude] = useState<boolean | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const mailable = recipients.filter((recipient) => recipient.email !== null);
    const hasClient = mailable.some((recipient) => recipient.roleKey === 'client');
    const available = setup?.option?.enclosable === true && hasClient;
    const checked = available && (include ?? true);
    const text = message ?? setup?.defaultMessage ?? '';
    const choice: ReportInvoiceChoice | undefined = checked
        ? { includeInvoice: true, message: text }
        : undefined;

    return {
        fields: {
            option: setup?.option,
            hasClient,
            hasOthers: mailable.some((recipient) => recipient.roleKey !== 'client'),
            checked,
            onCheckedChange: setInclude,
            message: text,
            onMessageChange: setMessage,
        },
        /** Wat de API meekrijgt; `undefined` als de factuur niet meegaat. */
        choice,
    };
}
